(function () {
  const state = {
    initialized: false,
    cleanups: [],
  };

  const onReady = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
      return;
    }

    callback();
  };

  const scheduleIdle = (callback, timeout = 1500) => {
    if ("requestIdleCallback" in window) {
      const handle = window.requestIdleCallback(callback, { timeout });
      return () => window.cancelIdleCallback(handle);
    }

    const handle = window.setTimeout(callback, timeout);
    return () => window.clearTimeout(handle);
  };

  const scheduleAfterPaint = (callback) => {
    let firstFrame = 0;
    let secondFrame = 0;

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(callback);
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  };

  const setupWhenVisible = (target, setup, options = {}) => {
    let started = false;
    let cleanup = () => {};
    let observer = null;

    const start = () => {
      if (started) {
        return;
      }

      started = true;
      observer?.disconnect();
      observer = null;
      cleanup = setup() || (() => {});
    };

    if (!target || !("IntersectionObserver" in window)) {
      start();
      return () => cleanup();
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          start();
        }
      },
      {
        rootMargin: options.rootMargin || "240px 0px",
        threshold: options.threshold ?? 0.01,
      }
    );

    observer.observe(target);

    return () => {
      observer?.disconnect();
      cleanup();
    };
  };

  const prepareReelVideo = (video, reducedMotion) => {
    if (reducedMotion) {
      video.pause();
      video.removeAttribute("autoplay");
      return;
    }

    const startAt = Number.parseFloat(video.dataset.start || "0");

    const setStartTime = () => {
      if (video.dataset.startApplied === "true") {
        return;
      }

      if (Number.isFinite(startAt) && startAt > 0) {
        const safeStart = video.duration
          ? Math.min(startAt, Math.max(video.duration - 0.1, 0))
          : startAt;

        try {
          video.currentTime = safeStart;
        } catch (error) {
          void error;
        }
      }

      video.dataset.startApplied = "true";
    };

    if (video.readyState >= 1) {
      setStartTime();
    } else {
      video.addEventListener("loadedmetadata", setStartTime, { once: true });
    }
  };

  const playReelVideo = (video, reducedMotion) => {
    if (reducedMotion) {
      return;
    }

    prepareReelVideo(video, reducedMotion);
    video.play().catch(() => {});
  };

  const populateProductPhotoSpirals = () => {
    document.querySelectorAll("[data-spiral-label]").forEach((spiralRoot) => {
      const label = spiralRoot.dataset.spiralLabel?.trim();
      const textPath = spiralRoot.querySelector("textPath");

      if (!label || !textPath) {
        return;
      }

      const ringLabel = label.toUpperCase();
      const repeatCount = Math.max(5, Math.ceil(112 / (ringLabel.length + 5)));
      const spiralCopy = `${Array.from({ length: repeatCount }, () => ringLabel).join("  •  ")}  •`;
      textPath.textContent = spiralCopy;
    });
  };

  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (character) => {
      const replacements = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      };

      return replacements[character] || character;
    });

  const formatMoney = (amount, currencyCode) => {
    const value = Number(amount || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: currencyCode || "INR",
        maximumFractionDigits: 2,
      }).format(value);
    } catch (error) {
      void error;
      return `₹${value.toFixed(2)}`;
    }
  };

  const setupCartDrawer = () => {
    const cartShell = document.querySelector("[data-cart-shell]");
    const cartDrawer = document.querySelector("[data-cart-drawer]");
    const cartItems = document.querySelector("[data-cart-items]");
    const cartEmpty = document.querySelector("[data-cart-empty]");
    const cartSubtotal = document.querySelector("[data-cart-subtotal]");
    const cartCheckout = document.querySelector("[data-cart-checkout]");
    const openButtons = Array.from(document.querySelectorAll("[data-open-cart]"));
    const closeButtons = Array.from(document.querySelectorAll("[data-cart-close]"));
    const cartCountNodes = Array.from(document.querySelectorAll("[data-cart-count]"));
    const addToCartForms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));

    if (!cartShell || !cartDrawer || !cartItems || !cartEmpty || !cartSubtotal || !cartCheckout) {
      return () => {};
    }

    let cartState = null;
    let cartLoaded = false;
    let cartFetchPromise = null;
    let isOpen = false;
    let lastFocusedElement = null;

    const root = window.Shopify?.routes?.root || "/";
    const initialItemCount = cartCountNodes.reduce((count, node) => {
      const parsedCount = Number.parseInt(node.textContent || "0", 10);
      return Number.isFinite(parsedCount) ? Math.max(count, parsedCount) : count;
    }, 0);
    let currentItemCount = initialItemCount;

    const setCheckoutState = (itemCount) => {
      const hasItems = itemCount > 0;
      cartCheckout.classList.toggle("is-disabled", !hasItems);
      cartCheckout.setAttribute("aria-disabled", hasItems ? "false" : "true");
      cartCheckout.tabIndex = hasItems ? 0 : -1;
      // Preserve any ?discount= param already appended by the offer script
      const existingDiscount = new URL(cartCheckout.href, window.location.origin).searchParams.get("discount");
      const baseHref = hasItems ? `${root}checkout` : "#";
      cartCheckout.href = (hasItems && existingDiscount)
        ? `${baseHref}?discount=${encodeURIComponent(existingDiscount)}`
        : baseHref;
    };

    const syncCartBadges = (itemCount) => {
      currentItemCount = itemCount;
      cartCountNodes.forEach((node) => {
        node.textContent = String(itemCount);
        node.hidden = itemCount < 1;
      });
    };

    const renderCartItems = (cart) => {
      const itemCount = Number(cart?.item_count || 0);
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const currency = cart?.currency || "INR";

      cartState = cart;
      cartLoaded = true;
      cartEmpty.hidden = itemCount > 0;
      cartItems.innerHTML = "";
      syncCartBadges(itemCount);
      setCheckoutState(itemCount);

      // Read inventory data embedded by Liquid
      const inventoryData = (() => {
        try { return JSON.parse(cartShell.dataset.inventory || "{}"); } catch { return {}; }
      })();

      // Price breakdown nodes
      const cartSubtotalNode   = document.querySelector("[data-cart-subtotal]");
      const cartCompareNode    = document.querySelector("[data-cart-compare]");
      const cartDiscountNode   = document.querySelector("[data-cart-discount]");
      const cartFinalNode      = document.querySelector("[data-cart-final]");
      const cartCompareRow     = document.querySelector("[data-cart-compare-row]");
      const cartDiscountRow    = document.querySelector("[data-cart-discount-row]");

      // Calculate totals — use Liquid-embedded compare_at_price per variant
      let totalCompare = 0;
      let totalSellingPrice = 0;
      items.forEach((item) => {
        const variantData = inventoryData[item.variant_id];
        const cp = (variantData?.compare && variantData.compare > item.price)
          ? variantData.compare
          : (item.original_price > item.price ? item.original_price : item.price);
        totalCompare += cp * item.quantity;
        // Use item.price (variant selling price) not cart.total_price which may include Cashfree discount
        totalSellingPrice += item.price * item.quantity;
      });
      const newPrice     = totalSellingPrice;
      const siteDiscount = totalCompare - newPrice;

      if (cartSubtotalNode) cartSubtotalNode.textContent = formatMoney(newPrice, currency);
      if (cartFinalNode)    cartFinalNode.textContent    = formatMoney(newPrice, currency);

      // Keep cart state in sync
      cartShell.dataset.cartQty    = String(itemCount);
      cartShell.dataset.cartAmount = String(newPrice);

      // Update add-to-cart / go-to-cart button state
      const cartToggleSlots = document.querySelectorAll("[data-cart-toggle-slot]");
      cartToggleSlots.forEach((slot) => {
        const variantId = Number(slot.dataset.variantId);
        const inCart = items.some((item) => item.variant_id === variantId);
        const addBtn = slot.querySelector("[data-add-to-cart-btn]");
        const goBtn  = slot.querySelector("[data-go-to-cart-btn]");
        if (addBtn) addBtn.style.display = inCart ? "none" : "";
        if (goBtn)  goBtn.style.display  = inCart ? "" : "none";
      });

      if (siteDiscount > 0) {
        if (cartCompareNode)  cartCompareNode.textContent  = formatMoney(totalCompare, currency);
        if (cartDiscountNode) cartDiscountNode.textContent = `− ${formatMoney(siteDiscount, currency)}`;
        if (cartCompareRow)   cartCompareRow.style.display  = "";
        if (cartDiscountRow)  cartDiscountRow.style.display = "";
      } else {
        if (cartCompareRow)  cartCompareRow.style.display  = "none";
        if (cartDiscountRow) cartDiscountRow.style.display = "none";
      }

      if (itemCount < 1) {
        return;
      }

      cartItems.innerHTML = items
        .map((item) => {
          const imageUrl = item.featured_image?.url || item.image || "";

          // Show only size, not price info
          const variantData = inventoryData[item.variant_id];
          const sizeLabel = item.variant_title && item.variant_title !== "Default Title"
            ? `<p class="cart-drawer-item-size">${escapeHtml(item.variant_title)}</p>` : "";

          // Stock indicator
          const displayQty = variantData?.stock ?? null;
          let stockHtml = "";
          if (displayQty !== null) {
            const color = displayQty < 500 ? "#ef4444" : displayQty < 1000 ? "#f59e0b" : "#16a34a";
            const label = displayQty < 500 ? `Only ${displayQty} left` : displayQty < 1000 ? `${displayQty} in stock` : `${displayQty} in stock`;
            stockHtml = `<p class="cart-drawer-stock" style="color:${color}"><span class="cart-drawer-stock-dot" style="background:${color}"></span>${escapeHtml(label)}</p>`;
          }

          return `
            <article class="cart-drawer-item">
              ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.product_title)}" class="cart-drawer-image" loading="lazy" decoding="async">` : ""}
              <div class="cart-drawer-item-copy">
                <h3>${escapeHtml(item.product_title)}</h3>
                ${sizeLabel}
                ${stockHtml}
                <div class="cart-drawer-quantity">
                  <button type="button" class="detail-quantity-btn" data-cart-action="decrement" data-line-key="${escapeHtml(item.key)}" aria-label="Decrease cart quantity">-</button>
                  <div class="cart-drawer-quantity-copy">
                    <span>Qty</span>
                    <strong>${item.quantity}</strong>
                  </div>
                  <button type="button" class="detail-quantity-btn" data-cart-action="increment" data-line-key="${escapeHtml(item.key)}" aria-label="Increase cart quantity">+</button>
                  <button type="button" class="detail-quantity-btn" data-cart-remove data-line-key="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.product_title)}">×</button>
                </div>
              </div>
            </article>
          `;
        })
        .join("");
    };

    const fetchCart = async ({ force = false } = {}) => {
      if (!force) {
        if (cartFetchPromise) {
          return cartFetchPromise;
        }

        if (cartLoaded && cartState) {
          return cartState;
        }
      }

      cartFetchPromise = fetch(`${root}cart.js`, {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("Cart couldn't be loaded.");
          }

          const cart = await response.json();
          renderCartItems(cart);
          return cart;
        })
        .finally(() => {
          cartFetchPromise = null;
        });

      return cartFetchPromise;
    };

    const setCartOpen = (nextOpen) => {
      if (nextOpen === isOpen) {
        return;
      }

      if (nextOpen) {
        lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      }

      isOpen = nextOpen;
      cartShell.classList.toggle("is-open", isOpen);
      cartShell.setAttribute("aria-hidden", isOpen ? "false" : "true");
      document.body.classList.toggle("is-cart-open", isOpen);

      if (isOpen) {
        cartDrawer.focus();
      } else if (lastFocusedElement && lastFocusedElement.isConnected && !lastFocusedElement.hidden) {
        lastFocusedElement.focus();
      }
    };

    const openCart = async () => {
      setCartOpen(true);
      if (!cartLoaded) {
        await fetchCart().catch(() => {});
      }
    };

    const handleDocumentKeydown = (event) => {
      if (event.key === "Escape" && isOpen) {
        setCartOpen(false);
      }
    };

    const updateLineQuantity = async (lineKey, nextQuantity) => {
      const response = await fetch(`${root}cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          id: lineKey,
          quantity: Math.max(0, nextQuantity),
        }),
      });

      if (!response.ok) {
        throw new Error("Cart couldn't be updated.");
      }

      const cart = await response.json();
      renderCartItems(cart);
      return cart;
    };

    const handleDrawerClick = async (event) => {
      const actionButton = event.target.closest("[data-cart-action]");
      const removeButton = event.target.closest("[data-cart-remove]");

      if (!actionButton && !removeButton) {
        return;
      }

      const lineKey = (actionButton || removeButton)?.dataset.lineKey || "";

      if (!lineKey || !cartState) {
        return;
      }

      const matchedItem = cartState.items.find((item) => item.key === lineKey);

      if (!matchedItem) {
        return;
      }

      const nextQuantity = removeButton
        ? 0
        : actionButton.dataset.cartAction === "increment"
          ? matchedItem.quantity + 1
          : matchedItem.quantity - 1;

      try {
        await updateLineQuantity(lineKey, nextQuantity);
      } catch (error) {
        window.alert(error instanceof Error ? error.message : "Cart couldn't be updated.");
      }
    };

    const handleCheckoutClick = (event) => {
      if (cartCheckout.classList.contains("is-disabled")) {
        event.preventDefault();
      }
    };

    const handleAddToCart = async (event) => {
      event.preventDefault();

      const form = event.currentTarget;
      const submitButton = form.querySelector('[type="submit"]');
      const formData = new FormData(form);
      const requestedQuantity = Math.max(1, Number.parseInt(String(formData.get("quantity") || "1"), 10) || 1);

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = true;
      }

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: formData,
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || "This item couldn't be added.");
        }

        setCartOpen(true);
        syncCartBadges(currentItemCount + requestedQuantity);
        setCheckoutState(currentItemCount);

        // Immediately toggle button state without waiting for fetch
        const variantId = Number(formData.get("id"));
        const cartToggleSlots = document.querySelectorAll("[data-cart-toggle-slot]");
        cartToggleSlots.forEach((slot) => {
          if (Number(slot.dataset.variantId) === variantId) {
            const addBtn = slot.querySelector("[data-add-to-cart-btn]");
            const goBtn  = slot.querySelector("[data-go-to-cart-btn]");
            if (addBtn) addBtn.style.display = "none";
            if (goBtn)  goBtn.style.display  = "";
          }
        });

        void fetchCart({ force: true }).catch(() => {});
      } catch (error) {
        window.alert(error instanceof Error ? error.message : "This item couldn't be added.");
      } finally {
        if (submitButton instanceof HTMLButtonElement) {
          submitButton.disabled = false;
        }
      }
    };

    const openHandlers = openButtons.map((button) => {
      const handler = async (event) => {
        event.preventDefault();
        await openCart();
      };
      button.addEventListener("click", handler);
      return [button, handler];
    });

    const warmCart = () => {
      if (initialItemCount < 1 || cartLoaded || cartFetchPromise) {
        return;
      }

      void fetchCart().catch(() => {});
    };

    const warmHandlers = openButtons.map((button) => {
      const handleWarm = () => warmCart();
      button.addEventListener("pointerenter", handleWarm, { passive: true });
      button.addEventListener("focus", handleWarm);
      return [button, handleWarm];
    });

    const cancelIdleWarmup = scheduleIdle(warmCart, 2400);

    const closeHandlers = closeButtons.map((button) => {
      const handler = () => setCartOpen(false);
      button.addEventListener("click", handler);
      return [button, handler];
    });

    const formHandlers = addToCartForms.map((form) => {
      form.addEventListener("submit", handleAddToCart);
      return form;
    });

    cartDrawer.addEventListener("click", handleDrawerClick);
    cartCheckout.addEventListener("click", handleCheckoutClick);
    document.addEventListener("keydown", handleDocumentKeydown);
    syncCartBadges(initialItemCount);
    setCheckoutState(initialItemCount);

    return () => {
      openHandlers.forEach(([button, handler]) => button.removeEventListener("click", handler));
      warmHandlers.forEach(([button, handler]) => {
        button.removeEventListener("pointerenter", handler);
        button.removeEventListener("focus", handler);
      });
      closeHandlers.forEach(([button, handler]) => button.removeEventListener("click", handler));
      formHandlers.forEach((form) => form.removeEventListener("submit", handleAddToCart));
      cartDrawer.removeEventListener("click", handleDrawerClick);
      cartCheckout.removeEventListener("click", handleCheckoutClick);
      document.removeEventListener("keydown", handleDocumentKeydown);
      cancelIdleWarmup();
    };
  };

  const setupTopbar = () => {
    const topbar = document.querySelector(".topbar");

    if (!topbar) {
      return () => {};
    }

    let isScrolled = window.scrollY > 24;
    let frame = 0;

    const commitTopbarState = () => {
      frame = 0;
      topbar.classList.toggle("is-scrolled", isScrolled);
    };

    const syncTopbarState = () => {
      const nextScrolled = window.scrollY > 24;

      if (nextScrolled === isScrolled) {
        return;
      }

      isScrolled = nextScrolled;

      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(commitTopbarState);
    };

    commitTopbarState();
    window.addEventListener("scroll", syncTopbarState, { passive: true });

    return () => {
      window.removeEventListener("scroll", syncTopbarState);
      window.cancelAnimationFrame(frame);
    };
  };

  const setupMobileNav = () => {
    const mobileNavShell = document.querySelector("[data-mobile-nav-shell]");
    const mobileNavDrawer = document.querySelector("[data-mobile-nav-drawer]");
    const mobileNavOpenButtons = Array.from(document.querySelectorAll("[data-mobile-nav-open]"));
    const mobileNavCloseButtons = Array.from(document.querySelectorAll("[data-mobile-nav-close]"));

    if (!mobileNavShell || !mobileNavDrawer || mobileNavOpenButtons.length === 0) {
      return () => {};
    }

    const mobileNavActionNodes = Array.from(
      mobileNavDrawer.querySelectorAll("a[href], button")
    ).filter((node) => !node.hasAttribute("data-mobile-nav-close"));

    let isOpen = false;
    let lastFocusedElement = null;

    const setMobileNavOpen = (nextOpen) => {
      if (nextOpen === isOpen) {
        return;
      }

      if (nextOpen) {
        lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      }

      isOpen = nextOpen;
      mobileNavShell.classList.toggle("is-open", isOpen);
      mobileNavShell.setAttribute("aria-hidden", isOpen ? "false" : "true");
      document.body.classList.toggle("is-mobile-nav-open", isOpen);

      mobileNavOpenButtons.forEach((button) => {
        button.setAttribute("aria-expanded", isOpen ? "true" : "false");
        button.classList.toggle("is-active", isOpen);
      });

      if (isOpen) {
        mobileNavDrawer.focus();
      } else if (lastFocusedElement && lastFocusedElement.isConnected && !lastFocusedElement.hidden) {
        lastFocusedElement.focus();
      }
    };

    const handleDocumentKeydown = (event) => {
      if (event.key === "Escape" && isOpen) {
        setMobileNavOpen(false);
      }
    };

    const handleWindowResize = () => {
      if (window.innerWidth > 860 && isOpen) {
        setMobileNavOpen(false);
      }
    };

    const openHandlers = mobileNavOpenButtons.map((button) => {
      const handler = () => setMobileNavOpen(true);
      button.addEventListener("click", handler);
      return [button, handler];
    });

    const closeHandlers = mobileNavCloseButtons.map((button) => {
      const handler = () => setMobileNavOpen(false);
      button.addEventListener("click", handler);
      return [button, handler];
    });

    const actionHandlers = mobileNavActionNodes.map((node) => {
      const handler = () => setMobileNavOpen(false);
      node.addEventListener("click", handler);
      return [node, handler];
    });

    document.addEventListener("keydown", handleDocumentKeydown);
    window.addEventListener("resize", handleWindowResize);

    return () => {
      openHandlers.forEach(([button, handler]) => button.removeEventListener("click", handler));
      closeHandlers.forEach(([button, handler]) => button.removeEventListener("click", handler));
      actionHandlers.forEach(([node, handler]) => node.removeEventListener("click", handler));
      document.removeEventListener("keydown", handleDocumentKeydown);
      window.removeEventListener("resize", handleWindowResize);
    };
  };

  const setupProductSlider = (sliderRoot, reducedMotion) => {
    const slides = Array.from(sliderRoot.querySelectorAll("[data-slider-slide]"));
    const dots = Array.from(sliderRoot.querySelectorAll("[data-slider-dot]"));
    const prevButton = sliderRoot.querySelector("[data-slider-prev]");
    const nextButton = sliderRoot.querySelector("[data-slider-next]");

    if (!slides.length) {
      return () => {};
    }

    let activeIndex = 0;
    let cycleTimer = null;
    let interactionPaused = false;
    let hiddenPaused = document.hidden;
    let sectionPaused = false;

    const clearCycleTimer = () => {
      if (cycleTimer !== null) {
        window.clearTimeout(cycleTimer);
        cycleTimer = null;
      }
    };

    const applyState = () => {
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
    };

    const scheduleAdvance = () => {
      clearCycleTimer();

      if (slides.length < 2 || reducedMotion || interactionPaused || hiddenPaused || sectionPaused) {
        return;
      }

      cycleTimer = window.setTimeout(() => {
        activeIndex = (activeIndex + 1) % slides.length;
        applyState();
        scheduleAdvance();
      }, 4200);
    };

    const goTo = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      applyState();
      scheduleAdvance();
    };

    const handlePrev = () => goTo(activeIndex - 1);
    const handleNext = () => goTo(activeIndex + 1);
    const handleVisibilityChange = () => {
      hiddenPaused = document.hidden;
      scheduleAdvance();
    };
    const pause = () => {
      interactionPaused = true;
      scheduleAdvance();
    };
    const resume = () => {
      interactionPaused = false;
      scheduleAdvance();
    };
    const handleFocusOut = (event) => {
      if (sliderRoot.contains(event.relatedTarget)) {
        return;
      }

      resume();
    };

    const dotHandlers = dots.map((dot, index) => {
      const handleDotClick = () => goTo(index);
      dot.addEventListener("click", handleDotClick);
      return handleDotClick;
    });

    prevButton?.addEventListener("click", handlePrev);
    nextButton?.addEventListener("click", handleNext);
    sliderRoot.addEventListener("mouseenter", pause);
    sliderRoot.addEventListener("mouseleave", resume);
    sliderRoot.addEventListener("focusin", pause);
    sliderRoot.addEventListener("focusout", handleFocusOut);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    let sliderObserver = null;

    if ("IntersectionObserver" in window) {
      sliderObserver = new IntersectionObserver(
        (entries) => {
          sectionPaused = !entries.some((entry) => entry.isIntersecting);
          scheduleAdvance();
        },
        {
          threshold: 0.45,
        }
      );

      sliderObserver.observe(sliderRoot);
    }

    applyState();
    scheduleAdvance();

    return () => {
      clearCycleTimer();
      sliderObserver?.disconnect();
      prevButton?.removeEventListener("click", handlePrev);
      nextButton?.removeEventListener("click", handleNext);
      sliderRoot.removeEventListener("mouseenter", pause);
      sliderRoot.removeEventListener("mouseleave", resume);
      sliderRoot.removeEventListener("focusin", pause);
      sliderRoot.removeEventListener("focusout", handleFocusOut);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      dots.forEach((dot, index) => dot.removeEventListener("click", dotHandlers[index]));
    };
  };

  const setupInfiniteReels = (reelsTrack, reducedMotion) => {
    const originalCards = Array.from(
      reelsTrack.querySelectorAll('.reel-card:not([data-clone="true"])')
    );

    if (!originalCards.length) {
      return () => {};
    }

    originalCards.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.dataset.clone = "true";
      clone.setAttribute("aria-hidden", "true");
      reelsTrack.appendChild(clone);
    });

    const reelVideos = reelsTrack.querySelectorAll(".reel-video");
    reelVideos.forEach((video) => prepareReelVideo(video, reducedMotion));

    let loopWidth = 0;
    let animationFrame = null;
    let lastTime = 0;
    let paused = true;
    let interactionPaused = false;
    let hiddenPaused = document.hidden;
    let sectionPaused = true;

    const measureLoopWidth = () => {
      const firstClone = reelsTrack.querySelector('.reel-card[data-clone="true"]');
      loopWidth = firstClone ? firstClone.offsetLeft - reelsTrack.firstElementChild.offsetLeft : 0;
    };

    const tick = (timestamp) => {
      if (!lastTime) {
        lastTime = timestamp;
      }

      const delta = timestamp - lastTime;
      lastTime = timestamp;

      if (!reducedMotion && !paused && loopWidth > 0) {
        const speed = window.innerWidth <= 720 ? 0.028 : 0.042;
        reelsTrack.scrollLeft += delta * speed;

        if (reelsTrack.scrollLeft >= loopWidth) {
          reelsTrack.scrollLeft -= loopWidth;
        }
      }

      if (!paused) {
        animationFrame = window.requestAnimationFrame(tick);
      } else {
        animationFrame = null;
        lastTime = 0;
      }
    };

    const syncPlaybackState = () => {
      paused = reducedMotion || interactionPaused || hiddenPaused || sectionPaused;

      if (paused) {
        reelVideos.forEach((video) => video.pause());

        if (animationFrame !== null) {
          window.cancelAnimationFrame(animationFrame);
          animationFrame = null;
        }

        lastTime = 0;
        return;
      }

      reelVideos.forEach((video) => playReelVideo(video, reducedMotion));

      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(tick);
      }
    };

    const pause = () => {
      interactionPaused = true;
      syncPlaybackState();
    };

    const resume = () => {
      interactionPaused = false;
      syncPlaybackState();
    };

    const handleVisibilityChange = () => {
      hiddenPaused = document.hidden;
      syncPlaybackState();
    };

    measureLoopWidth();

    reelsTrack.addEventListener("mouseenter", pause);
    reelsTrack.addEventListener("mouseleave", resume);
    reelsTrack.addEventListener("focusin", pause);
    reelsTrack.addEventListener("focusout", resume);
    reelsTrack.addEventListener("pointerdown", pause);
    reelsTrack.addEventListener("pointerup", resume);
    reelsTrack.addEventListener("touchstart", pause, { passive: true });
    reelsTrack.addEventListener("touchend", resume, { passive: true });
    window.addEventListener("resize", measureLoopWidth);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const reelsObserver = new IntersectionObserver(
      (entries) => {
        sectionPaused = !entries.some((entry) => entry.isIntersecting);
        syncPlaybackState();
      },
      {
        threshold: 0.2,
      }
    );

    reelsObserver.observe(reelsTrack.closest("section") || reelsTrack);
    syncPlaybackState();

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      reelsObserver.disconnect();
      window.removeEventListener("resize", measureLoopWidth);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      reelsTrack.removeEventListener("mouseenter", pause);
      reelsTrack.removeEventListener("mouseleave", resume);
      reelsTrack.removeEventListener("focusin", pause);
      reelsTrack.removeEventListener("focusout", resume);
      reelsTrack.removeEventListener("pointerdown", pause);
      reelsTrack.removeEventListener("pointerup", resume);
      reelsTrack.removeEventListener("touchstart", pause);
      reelsTrack.removeEventListener("touchend", resume);
      reelVideos.forEach((video) => video.pause());
      reelsTrack.querySelectorAll('.reel-card[data-clone="true"]').forEach((clone) => clone.remove());
    };
  };

  const setupTestimonialRotator = (testimonialsStack, reducedMotion) => {
    if (reducedMotion) {
      return () => {};
    }

    const testimonialCards = Array.from(testimonialsStack.querySelectorAll(".quote-card"));

    if (testimonialCards.length < 2) {
      return () => {};
    }

    let activeIndex = 0;
    let cycleTimer = null;
    let transitionTimer = null;
    let hiddenPaused = document.hidden;
    let sectionPaused = true;
    let isAnimating = false;

    const nextIndexFor = (index) => (index + 1) % testimonialCards.length;

    const measureTestimonialsHeight = () => {
      // Temporarily make all cards static to get accurate natural heights
      testimonialCards.forEach((card) => {
        card.style.position = "static";
        card.style.opacity = "1";
      });
      const tallestCard = testimonialCards.reduce((height, card) => Math.max(height, card.offsetHeight), 0);
      testimonialCards.forEach((card) => {
        card.style.position = "";
        card.style.opacity = "";
      });

      if (tallestCard > 0) {
        testimonialsStack.style.height = `${tallestCard + 38}px`;
      }
    };

    const applySettledState = () => {
      testimonialCards.forEach((card, index) => {
        card.classList.remove("is-active", "is-next", "is-entering", "is-leaving");

        if (index === activeIndex) {
          card.classList.add("is-active");
        } else if (index === nextIndexFor(activeIndex)) {
          card.classList.add("is-next");
        }
      });
    };

    const clearCycleTimer = () => {
      if (cycleTimer !== null) {
        window.clearTimeout(cycleTimer);
        cycleTimer = null;
      }
    };

    const clearTransitionTimer = () => {
      if (transitionTimer !== null) {
        window.clearTimeout(transitionTimer);
        transitionTimer = null;
      }
    };

    const scheduleAdvance = () => {
      clearCycleTimer();

      if (hiddenPaused || sectionPaused || isAnimating) {
        return;
      }

      cycleTimer = window.setTimeout(() => {
        const currentIndex = activeIndex;
        const incomingIndex = nextIndexFor(currentIndex);
        const currentCard = testimonialCards[currentIndex];
        const incomingCard = testimonialCards[incomingIndex];

        if (!currentCard || !incomingCard) {
          return;
        }

        isAnimating = true;
        testimonialCards.forEach((card) => {
          card.classList.remove("is-active", "is-next", "is-entering", "is-leaving");
        });

        currentCard.classList.add("is-leaving");
        incomingCard.classList.add("is-entering");
        clearTransitionTimer();

        transitionTimer = window.setTimeout(() => {
          activeIndex = incomingIndex;
          isAnimating = false;
          applySettledState();
          scheduleAdvance();
        }, 560);
      }, 2200);
    };

    const handleVisibilityChange = () => {
      hiddenPaused = document.hidden;
      scheduleAdvance();
    };

    testimonialsStack.classList.add("is-animated");
    applySettledState();
    measureTestimonialsHeight();

    window.addEventListener("load", measureTestimonialsHeight, { once: true });
    window.addEventListener("resize", measureTestimonialsHeight);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const testimonialsObserver = new IntersectionObserver(
      (entries) => {
        sectionPaused = !entries.some((entry) => entry.isIntersecting);
        scheduleAdvance();
      },
      {
        threshold: 0.35,
      }
    );

    testimonialsObserver.observe(testimonialsStack.closest("section") || testimonialsStack);
    scheduleAdvance();

    return () => {
      clearCycleTimer();
      clearTransitionTimer();
      testimonialsObserver.disconnect();
      window.removeEventListener("resize", measureTestimonialsHeight);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  };

  const setupReveals = (_reducedMotion) => {
    const revealNodes = document.querySelectorAll(".reveal");
    revealNodes.forEach((node) => node.classList.add("is-visible"));
    return () => {};
  };

  const setupResponsiveReinit = () => {
    let viewportMode = window.innerWidth <= 860 ? "compact" : "wide";
    let frame = 0;

    const handleResize = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const nextViewportMode = window.innerWidth <= 860 ? "compact" : "wide";

        if (nextViewportMode !== viewportMode) {
          viewportMode = nextViewportMode;
          init();
        }
      });
    };

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.cancelAnimationFrame(frame);
    };
  };

  const init = () => {
    if (state.initialized) {
      state.cleanups.forEach((cleanup) => cleanup());
    }

    state.initialized = true;
    state.cleanups = [];

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    state.cleanups.push(setupTopbar());
    state.cleanups.push(setupMobileNav());
    state.cleanups.push(setupCartDrawer());
    state.cleanups.push(setupResponsiveReinit());

    const visibleProductSliders = Array.from(document.querySelectorAll("[data-product-slider]")).filter((sliderRoot) => {
      const styles = window.getComputedStyle(sliderRoot);
      return styles.display !== "none" && styles.visibility !== "hidden";
    });

    visibleProductSliders.forEach((sliderRoot) => {
      state.cleanups.push(setupProductSlider(sliderRoot, reducedMotion));
    });

    state.cleanups.push(
      scheduleAfterPaint(() => {
        populateProductPhotoSpirals();
        state.cleanups.push(setupReveals(reducedMotion));

        document.querySelectorAll("[data-reels-track]").forEach((reelsTrack) => {
          state.cleanups.push(
            setupWhenVisible(
              reelsTrack.closest("section") || reelsTrack,
              () => setupInfiniteReels(reelsTrack, reducedMotion),
              { rootMargin: "280px 0px", threshold: 0.01 }
            )
          );
        });

        document.querySelectorAll("[data-testimonials-stack]").forEach((stack) => {
          state.cleanups.push(
            setupWhenVisible(
              stack.closest("section") || stack,
              () => setupTestimonialRotator(stack, reducedMotion),
              { rootMargin: "220px 0px", threshold: 0.01 }
            )
          );
        });
      })
    );
  };

  onReady(init);
  document.addEventListener("shopify:section:load", init);
})();
